// pages/SafetyForm.jsx - Main Safety Assessment Form with Required Field Validation
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import WeatherInfo from '../components/WeatherInfo';
import ExistingSiteConditions from '../components/ExistingSiteConditions';
import HazardChecklist from '../components/HazardChecklist';
import WorkPlan from '../components/WorkPlan';
import CrewSection from '../components/CrewSection';
import { getFormData, submitForm } from '../services/api';

const SafetyForm = () => {
  const [searchParams] = useSearchParams();
  const { code: urlCode } = useParams();
  const shortCode = urlCode || searchParams.get('code');

  // Refs for scrolling to errors
  const treeInspectionRef = useRef(null);
  const electricalInspectionRef = useRef(null);
  const workZoneSecurityRef = useRef(null);
  const groundControlRef = useRef(null);
  const call911Ref = useRef(null);
  const crewSignaturesRef = useRef(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Job data from API
  const [jobData, setJobData] = useState(null);

  // Submission tracking
  const [previouslySubmitted, setPreviouslySubmitted] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [submissionCount, setSubmissionCount] = useState(0);
  
  // Track if data was actually prefilled (not just that a previous submission exists)
  const [wasPrefilled, setWasPrefilled] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    hazards: null,
    siteConditions: null,
    workPlan: null,
    crew: [{ name: '', signature: null, signedAt: null }]
  });

  // Load form data on mount
  useEffect(() => {
    if (!shortCode) {
      setError('No form code provided');
      setLoading(false);
      return;
    }

    loadFormData();
  }, [shortCode]);

  const loadFormData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getFormData(shortCode);
      
      console.log('📥 Loaded form data:', {
        previouslySubmitted: data.previouslySubmitted,
        submissionCount: data.submissionCount,
        hasFormData: !!data.lastSubmission?.formData,
        formDataKeys: data.lastSubmission?.formData ? Object.keys(data.lastSubmission.formData) : []
      });
      
      setJobData(data);
      setPreviouslySubmitted(data.previouslySubmitted || false);
      setLastSubmission(data.lastSubmission || null);
      setSubmissionCount(data.submissionCount || 0);

      // PREFILL FROM PREVIOUS SUBMISSION (if resubmission)
      if (data.previouslySubmitted && data.lastSubmission?.formData) {
        const previousData = data.lastSubmission.formData;
        
        console.log('📋 Prefilling form from previous submission...');
        console.log('   Previous hazards:', previousData.hazards ? Object.keys(previousData.hazards.identified || {}).length + ' identified' : 'none');
        console.log('   Previous workPlan:', !!previousData.workPlan);
        console.log('   Previous siteConditions:', !!previousData.siteConditions);
        console.log('   Previous crew:', previousData.crew?.length || 0);
        
        setFormData({
          // Carry over site conditions (photos won't load but notes will)
          siteConditions: previousData.siteConditions || null,
          
          // Carry over hazards and mitigations
          hazards: previousData.hazards || null,
          
          // Carry over work plan
          workPlan: previousData.workPlan || null,
          
          // Carry over crew NAMES but clear signatures (must re-sign)
          crew: previousData.crew?.map(member => ({
            name: member.name,
            signature: null,      // Must re-sign
            signedAt: null
          })) || [{ name: '', signature: null, signedAt: null }]
        });
        
        // Mark that we actually prefilled data
        setWasPrefilled(true);
        
        console.log('✅ Form prefilled from previous submission');
        
      } else {
        // No previous submission data available
        if (data.previouslySubmitted) {
          console.warn('⚠️ Previous submission exists but formData is missing');
        }
        
        // Pre-populate crew from Jobber if available
        if (data.crewMembers?.length > 0) {
          setFormData(prev => ({
            ...prev,
            crew: data.crewMembers.map(member => ({
              name: member.name,
              signature: null,
              signedAt: null
            }))
          }));
        }
      }

    } catch (err) {
      console.error('Error loading form:', err);
      setError(err.message || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  // Update form section
  const updateFormSection = (section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = [];

    // 1. Tree inspection checkbox
    if (!formData.hazards?.inspections?.tree) {
      errors.push({
        message: 'Visual tree inspection must be completed',
        ref: treeInspectionRef,
        section: 'Tree Hazards'
      });
    }

    // 2. Electrical inspection checkbox
    if (!formData.hazards?.inspections?.electrical) {
      errors.push({
        message: 'Visual electrical inspection must be completed',
        ref: electricalInspectionRef,
        section: 'Electrical Hazards'
      });
    }

    // 3. Work zone security plan
    if (!formData.workPlan?.workZoneSecurity?.trim()) {
      errors.push({
        message: 'Work zone security plan is required',
        ref: workZoneSecurityRef,
        section: 'Work Plan'
      });
    }

    // 4. Ground control assignment
    if (!formData.workPlan?.crewAssignments?.groundControl) {
      errors.push({
        message: 'Ground Control crew assignment is required',
        ref: groundControlRef,
        section: 'Work Plan'
      });
    }

    // 5. Call 911 assignment
    if (!formData.workPlan?.crewAssignments?.call911) {
      errors.push({
        message: 'Call 911 crew assignment is required',
        ref: call911Ref,
        section: 'Work Plan'
      });
    }

    // 6. At least one crew member with signature
    const signedCrew = formData.crew.filter(m => m.name?.trim() && m.signature);
    if (signedCrew.length === 0) {
      errors.push({
        message: 'At least one crew member must sign the form',
        ref: crewSignaturesRef,
        section: 'Crew Signatures'
      });
    }

    // 7. All crew members with names must have signatures
    const namedCrew = formData.crew.filter(m => m.name?.trim());
    const unsignedCrew = namedCrew.filter(m => !m.signature);
    if (unsignedCrew.length > 0) {
      errors.push({
        message: `${unsignedCrew.length} crew member(s) need to sign: ${unsignedCrew.map(m => m.name).join(', ')}`,
        ref: crewSignaturesRef,
        section: 'Crew Signatures'
      });
    }

    // 8. Underground utility locates selection required
    if (!formData.hazards?.undergroundLocates) {
      errors.push({
        message: 'Underground utility locates status must be selected',
        section: 'Underground Hazards'
      });
    }

    // 9. All selected hazards must have mitigation text
    const HAZARDS_REQUIRING_MITIGATION = {
      // Weather
      heat: 'Extreme Heat',
      cold: 'Extreme Cold',
      wind: 'High Winds',
      rain: 'Rain/Wet Conditions',
      lightning: 'Lightning Risk',
      ice: 'Ice/Snow',
      // Work Zone
      slips_trips: 'Slips, Trips, Falls',
      traffic: 'Traffic',
      targets: 'Targets',
      pedestrians: 'Pedestrians',
      poor_visibility: 'Poor Visibility',
      water: 'Water',
      steep_slope: 'Steep Slope',
      workzone_other: 'Other (Work Zone)',
      // Tree
      large_tree: 'Large Tree/Fatigue Hazard',
      rotted_base: 'Rotted Base/Roots',
      mushrooms: 'Mushrooms on Trunk',
      cavities: 'Cavities/Hollows',
      cracked_trunk: 'Cracked Trunk',
      very_dead: 'Very Dead',
      bees_animals: 'Bees/Animals in Tree',
      severe_lean: 'Severe Lean',
      species_concerns: 'Species Specific Concerns',
      tree_other: 'Other (Tree)',
      // Equipment
      chipper: 'Chipper',
      chainsaw: 'Chainsaw',
      lift_crane: 'Lift or Crane',
      loader_excavator: 'Loader/Skidsteer/Excavator',
      stump_grinder: 'Stump Grinder',
      equipment_other: 'Other (Equipment)',
      // Electrical
      overhead_wires: 'Overhead Wires',
      electrical_boxes: 'Electrical Boxes',
      electrical_other: 'Other (Electrical)',
      // Underground
      soft_ground: 'Soft/Sensitive Ground',
      septic: 'Septic Tank or Drainfield',
      underground_other: 'Other (Underground)',
      // Unfamiliar
      unfamiliar_tools: 'Tools, Equipment, or Systems',
      unfamiliar_personnel: 'Personnel',
      unfamiliar_species: 'Species',
      unfamiliar_other: 'Other (Unfamiliar)'
    };

    if (formData.hazards?.identified) {
      const identifiedHazards = Object.keys(formData.hazards.identified);
      const missingMitigations = [];

      identifiedHazards.forEach(hazardId => {
        // Check if this hazard requires mitigation
        if (HAZARDS_REQUIRING_MITIGATION[hazardId]) {
          const mitigation = formData.hazards.mitigations?.[hazardId];
          if (!mitigation || !mitigation.trim()) {
            missingMitigations.push(HAZARDS_REQUIRING_MITIGATION[hazardId]);
          }
        }
      });

      if (missingMitigations.length > 0) {
        errors.push({
          message: `Mitigation required for: ${missingMitigations.join(', ')}`,
          section: 'Hazard Mitigations'
        });
      }
    }

    return errors;
  };

  // Handle submit with validation
  const handleSubmit = async () => {
    // Clear previous errors
    setValidationErrors([]);

    // Validate form
    const errors = validateForm();
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      
      // Scroll to first error
      if (errors[0].ref?.current) {
        errors[0].ref.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
      
      return;
    }

    try {
      setSubmitting(true);

      const submitData = {
        jobInfo: {
          jobId: jobData.jobId,
          jobNumber: jobData.jobNumber,
          title: jobData.title,
          clientName: jobData.clientName,
          address: jobData.address?.formatted
        },
        weather: jobData.weather,
        hospital: jobData.hospital,
        hazards: formData.hazards,
        siteConditions: formData.siteConditions,
        workPlan: formData.workPlan,
        crew: formData.crew.filter(m => m.name && m.signature),
        submittedAt: new Date().toISOString()
      };

      const result = await submitForm(shortCode, submitData);
      
      setSubmitted(true);
      setSubmissionCount(result.submissionNumber || submissionCount + 1);

    } catch (err) {
      console.error('Error submitting form:', err);
      alert('Failed to submit: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F1F0E9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌲</div>
          <p style={{ color: '#5D757E' }}>Loading safety form...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F1F0E9',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center',
          maxWidth: '400px',
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ marginBottom: '8px', color: '#dc2626' }}>Error Loading Form</h2>
          <p style={{ color: '#5D757E', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={loadFormData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e8f5f0',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center',
          maxWidth: '400px',
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ marginBottom: '8px', color: '#388523' }}>
            {submissionCount > 1 ? 'Assessment Resubmitted!' : 'Assessment Submitted!'}
          </h2>
          <p style={{ color: '#5D757E', marginBottom: '8px' }}>
            Job #{jobData.jobNumber} - {jobData.clientName}
          </p>
          {submissionCount > 1 && (
            <p style={{ 
              color: '#032B3A', 
              fontSize: '14px',
              backgroundColor: '#F5F0D1',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #CDB52D'
            }}>
              This is revision {submissionCount}
            </p>
          )}
          <p style={{ color: '#5D757E', marginTop: '16px', fontSize: '14px' }}>
            The safety assessment has been attached to the job in Jobber.
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F1F0E9'
    }}>
      {/* Pilot Feedback Button - Fixed top right */}
      <a
        href="https://docs.google.com/forms/d/e/1FAIpQLSdiKwPXwLLGcMIqwh-OFmxo64C1xlHJ49wX9e291BSaadOWtQ/viewform?usp=dialog"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: 'fixed',
          top: '12px',
          right: '12px',
          padding: '6px 12px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: '#233D48',
          fontSize: '12px',
          fontWeight: '500',
          textDecoration: 'none',
          borderRadius: '6px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #DADFE2',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        💬 Feedback
      </a>

      {/* Header */}
      <div style={{
        backgroundColor: '#388523',
        color: 'white',
        padding: '16px 20px'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {jobData.tenant?.companyName && (
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>
              {jobData.tenant.companyName}
            </div>
          )}
          <h1 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
            Jobsite Safety Assessment
          </h1>
          <p style={{ fontSize: '14px', opacity: 0.9 }}>
            Job #{jobData.jobNumber} • {jobData.clientName}
          </p>
          {jobData.address?.formatted && (
            <p style={{ fontSize: '13px', opacity: 0.7, marginTop: '4px' }}>
              📍 {jobData.address.formatted}
            </p>
          )}
        </div>
      </div>

      {/* Resubmission Banner - Shows when a previous submission exists */}
      {previouslySubmitted && (
        <div style={{
          backgroundColor: '#F5F0D1',
          borderLeft: '4px solid #CDB52D',
          padding: '12px 20px'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '10px',
              color: '#032B3A'
            }}>
              <span style={{ fontSize: '20px' }}>🔄</span>
              <div>
                <strong>A JSA has already been submitted for this job</strong>
                {lastSubmission && (
                  <p style={{ fontSize: '13px', marginTop: '4px', color: '#233D48' }}>
                    Last submitted on {new Date(lastSubmission.submittedAt).toLocaleDateString()} 
                    {' '}at {new Date(lastSubmission.submittedAt).toLocaleTimeString()}
                    {lastSubmission.submittedBy && ` by ${lastSubmission.submittedBy}`}
                    {submissionCount > 1 && ` (${submissionCount} total submissions)`}
                  </p>
                )}
                <p style={{ fontSize: '13px', marginTop: '4px', color: '#5D757E' }}>
                  Submitting again will create a new revision attached to this job.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div style={{
          backgroundColor: '#FAECEA',
          borderLeft: '4px solid #D24232',
          padding: '16px 20px'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '10px',
              color: '#032B3A'
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '8px', color: '#D24232' }}>
                  Please complete the following required fields:
                </strong>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {validationErrors.map((err, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      <button
                        onClick={() => {
                          if (err.ref?.current) {
                            err.ref.current.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'center' 
                            });
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#D24232',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: '14px',
                          textAlign: 'left'
                        }}
                      >
                        {err.message}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prefill Indicator - Only shows when data was ACTUALLY prefilled */}
      {wasPrefilled && (
        <div style={{
          backgroundColor: '#e8f5f0',
          borderBottom: '1px solid #388523',
          padding: '12px 20px'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '10px',
              color: '#388523'
            }}>
              <span style={{ fontSize: '20px' }}>📋</span>
              <div>
                <strong>Form prefilled from previous assessment</strong>
                <p style={{ fontSize: '13px', marginTop: '4px', opacity: 0.9 }}>
                  Review all sections and update as needed. All crew members must sign again.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        
        {/* Hospital Info */}
        {jobData.hospital && (
          <div style={{
            backgroundColor: '#E2F1FD',
            borderRadius: '0 8px 8px 0',
            padding: '12px 16px',
            marginBottom: '16px',
            borderLeft: '4px solid #3B9AE8'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '4px'
            }}>
              <span>🏥</span>
              <strong style={{ color: '#032B3A' }}>Nearest Hospital</strong>
            </div>
            <p style={{ fontSize: '14px', margin: '0', color: '#032B3A' }}>{jobData.hospital.name}</p>
            <p style={{ fontSize: '13px', color: '#233D48', margin: '2px 0' }}>
              {jobData.hospital.address} ({jobData.hospital.distance} miles)
            </p>
            {jobData.hospital.phone && (
              <a 
                href={`tel:${jobData.hospital.phone}`}
                style={{ fontSize: '14px', color: '#3B9AE8' }}
              >
                📞 {jobData.hospital.phone}
              </a>
            )}
          </div>
        )}

        {/* Weather */}
        <WeatherInfo weather={jobData.weather} />

        {/* Hazard Checklist */}
        <div ref={treeInspectionRef}>
          <div ref={electricalInspectionRef}>
            <HazardChecklist
              data={formData.hazards}
              onChange={(data) => updateFormSection('hazards', data)}
              weather={jobData.weather}
            />
          </div>
        </div>

        {/* Existing Site Conditions */}
        <ExistingSiteConditions
          data={formData.siteConditions}
          onChange={(data) => updateFormSection('siteConditions', data)}
        />

        {/* Work Plan */}
        <div ref={workZoneSecurityRef}>
          <div ref={groundControlRef}>
            <div ref={call911Ref}>
              <WorkPlan
                data={formData.workPlan}
                onChange={(data) => updateFormSection('workPlan', data)}
                crewMembers={formData.crew}
                jobInfo={jobData}
              />
            </div>
          </div>
        </div>

        {/* Crew Signatures */}
        <div ref={crewSignaturesRef}>
          <CrewSection
            data={formData.crew}
            onChange={(data) => updateFormSection('crew', data)}
          />
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: '24px', marginBottom: '40px' }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: submitting ? '#9ca3af' : '#388523',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {submitting ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : previouslySubmitted ? (
              <>
                🔄 Resubmit Safety Assessment
              </>
            ) : (
              <>
                ✓ Submit Safety Assessment
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #ffffff40;
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SafetyForm;